import { useLiveQuery } from "dexie-react-hooks";
import { CalendarClock, Keyboard, Plus, Smartphone, Sparkles, Sun, Type, X, type LucideIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { dueFromDateTime, toISODate, type Due, type Task } from "@todoer/shared";
import { moveDue } from "../components/CalendarView.js";
import { DuePicker } from "../components/DuePicker.js";
import { HabitsToday } from "../components/Habits.js";
import { openQuickAdd } from "../components/Layout.js";
import { Dialog, Popover, usePopover } from "../components/overlay.js";
import { sortTasks, TaskList, TaskRow } from "../components/TaskList.js";
import { Button, cx, EmptyState, IconButton, PageHeader, SectionHeader } from "../components/ui.js";
import { db } from "../lib/db.js";
import { friendlyDate, shortDate } from "../lib/format.js";
import { useNow } from "../lib/hooks.js";
import { isStandalone, startInstall } from "../lib/install.js";
import { cmd, dispatch } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";

const TIPS_KEY = "todoer.tipsDismissed";

export function Today() {
  const now = useNow();
  const today = toISODate(now);
  const [planning, setPlanning] = useState(false);
  const reschedule = usePopover<HTMLButtonElement>();
  const tasks = useLiveQuery(() => db.tasks.filter((t) => !t.completedAt && !!t.due && t.due.date <= today).toArray(), [today]);
  const doneToday = useLiveQuery(() => db.tasks.filter((t) => !!t.completedAt && toISODate(new Date(t.completedAt)) === today).count(), [today], 0);
  const overdue = sortTasks((tasks ?? []).filter((t) => t.due!.date < today));
  const dueToday = sortTasks((tasks ?? []).filter((t) => t.due!.date === today));
  const defaultDue = useMemo(() => dueFromDateTime(today, null), [today]);

  const rescheduleOverdue = async (due: Due | null) => {
    const list = overdue;
    reschedule.close();
    await dispatch(...list.map((t) => cmd("item_update", { id: t.id, due: due ? moveDue(t.due, due.date) : null })));
    showToast(`Moved ${list.length} overdue task${list.length === 1 ? "" : "s"} to ${due ? friendlyDate(due.date).toLowerCase() : "no date"}`, {
      label: "Undo", run: () => dispatch(...list.map((t) => cmd("item_update", { id: t.id, due: t.due }))),
    });
  };

  const loaded = tasks !== undefined;
  const empty = loaded && !overdue.length && !dueToday.length;
  return (
    <div>
      <PageHeader title="Today"
        subtitle={now.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
        actions={<Button variant="outline-brand" icon={Sun} onClick={() => setPlanning(true)} aria-label="Plan my day"><span className="max-sm:hidden">Plan my day</span><span className="sm:hidden">Plan</span></Button>}
        below={loaded && (
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Summary">
            <Stat value={tasks!.length} label="to do" />
            <Stat value={doneToday} label="done" tone="text-success" />
            {overdue.length > 0 && <Stat value={overdue.length} label="overdue" tone="text-danger" />}
          </div>
        )} />
      <FirstRunTips />
      <HabitsToday />
      {overdue.length > 0 && (
        <section className="mb-8" aria-labelledby="overdue-h">
          <SectionHeader id="overdue-h" title="Overdue" tone="text-danger" count={overdue.length}
            actions={<Button size="sm" icon={CalendarClock} onClick={reschedule.toggle} aria-expanded={reschedule.isOpen}>Reschedule</Button>} />
          <ul>{overdue.map((t) => <TaskRow key={t.id} task={t} showProject />)}</ul>
        </section>
      )}
      {reschedule.anchor && (
        <Popover anchor={reschedule.anchor} onClose={reschedule.close} title={`Reschedule ${overdue.length} overdue`} align="end" width={300}>
          <DuePicker dateOnly value={null} onChange={(d) => void rescheduleOverdue(d)} />
        </Popover>
      )}
      {empty ? (
        <EmptyState kind="today" title="Nothing due today" action={<Button variant="primary" icon={Plus} onClick={openQuickAdd} shortcut="Q">Add a task</Button>}>
          Enjoy the calm, or plan something for later.
        </EmptyState>
      ) : (
        <section aria-labelledby={overdue.length ? "today-h" : undefined}>
          {overdue.length > 0 && <SectionHeader id="today-h" title={`Today · ${shortDate(today)}`} count={dueToday.length} />}
          <TaskList tasks={dueToday} showProject defaultDue={defaultDue} />
        </section>
      )}
      {planning && <PlanMyDay today={today} onClose={() => setPlanning(false)} />}
    </div>
  );
}

function Stat({ value, label, tone }: { value: number; label: string; tone?: string }) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-surface-raised px-3 text-meta text-text-muted">
      <span className={cx("tabular font-semibold text-text", tone)}>{value}</span>{label}
    </span>
  );
}

function FirstRunTips() {
  const [dismissed, setDismissed] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem(TIPS_KEY) ?? "[]"); } catch { return []; } });
  const tips: { id: string; icon: LucideIcon; title: string; body: string; action?: { label: string; run: () => void } }[] = [
    { id: "q", icon: Keyboard, title: "Press Q to add a task", body: "From anywhere. On your phone, tap the red + button." },
    { id: "dates", icon: Type, title: "Type dates naturally", body: "“Call Sam tomorrow 3pm p1 #Work” sets the date, priority and project." },
    ...(isStandalone() ? [] : [{
      id: "install", icon: Smartphone, title: "Install the app",
      body: "Put Todoer on your home screen or desktop. It opens full screen and can send reminders.",
      action: { label: "Install", run: () => void startInstall() },
    }]),
  ];
  const visible = tips.filter((t) => !dismissed.includes(t.id));
  if (!visible.length) return null;
  const dismiss = (id: string) => setDismissed((d) => {
    const next = [...d, id];
    try { localStorage.setItem(TIPS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  return (
    <section aria-label="Tips" className="no-scrollbar -mx-4 mb-6 flex snap-x gap-3 overflow-x-auto px-4 md:mx-0 md:grid md:grid-cols-3 md:px-0">
      {visible.map((t) => {
        const Icon = t.icon;
        return (
          <div key={t.id} className="relative w-[78vw] shrink-0 snap-start rounded-card border border-border bg-surface-raised p-4 md:w-auto">
            <span className="mb-3 grid h-8 w-8 place-items-center rounded-control bg-brand-soft text-brand-text"><Icon size={16} aria-hidden /></span>
            <p className="pr-6 text-body font-semibold">{t.title}</p>
            <p className="mt-0.5 text-meta text-text-muted">{t.body}</p>
            {t.action && <Button size="sm" variant="primary" className="mt-3" onClick={t.action.run}>{t.action.label}</Button>}
            <IconButton size="sm" icon={X} label={`Dismiss tip: ${t.title}`} className="absolute right-2 top-2" onClick={() => dismiss(t.id)} />
          </div>
        );
      })}
    </section>
  );
}

/** Suggests tasks to pull into today: overdue, due soon, and high-priority undated ones. */
function PlanMyDay({ today, onClose }: { today: string; onClose: () => void }) {
  const weekAhead = toISODate(new Date(Date.now() + 7 * 86_400_000));
  const data = useLiveQuery(async () => {
    const active = await db.tasks.filter((t) => !t.completedAt && !t.parentId).toArray();
    return {
      overdue: sortTasks(active.filter((t) => t.due && t.due.date < today)),
      soon: sortTasks(active.filter((t) => t.due && t.due.date > today && t.due.date <= weekAhead)),
      undated: active.filter((t) => !t.due).sort((a, b) => b.priority - a.priority || (a.deadline ?? "9").localeCompare(b.deadline ?? "9")).slice(0, 15),
      todayCount: active.filter((t) => t.due?.date === today).length,
    };
  }, [today, weekAhead]);
  const add = (t: Task) => void dispatch(cmd("item_update", { id: t.id, due: moveDue(t.due, today) }));
  const group = (title: string, list: Task[]) => list.length > 0 && (
    <section className="mb-5">
      <h3 className="mb-1 text-caption font-semibold uppercase text-text-faint">{title}</h3>
      <ul>
        {list.map((t) => (
          <li key={t.id} className="flex min-h-11 items-center gap-3 border-b border-border py-1.5 text-body">
            <span className="min-w-0 flex-1 truncate">{t.content}</span>
            {t.due && <span className="text-meta text-text-faint">{friendlyDate(t.due.date)}</span>}
            <Button size="sm" variant="outline-brand" icon={Plus} onClick={() => add(t)} aria-label={`Add “${t.content}” to today`}>Today</Button>
          </li>
        ))}
      </ul>
    </section>
  );
  return (
    <Dialog title="Plan my day" size="lg" onClose={onClose} description={`${data?.todayCount ?? 0} task${data?.todayCount === 1 ? "" : "s"} planned for today`}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <p className="mb-4 flex items-center gap-2 text-body text-text-muted"><Sparkles size={16} className="text-brand-text" aria-hidden />Pull in anything else you want to get done today.</p>
      {data && group("Overdue", data.overdue)}
      {data && group("Due in the next 7 days", data.soon)}
      {data && group("No date, highest priority first", data.undated)}
      {data && !data.overdue.length && !data.soon.length && !data.undated.length && <EmptyState compact kind="today" title="Nothing else waiting" />}
    </Dialog>
  );
}
